import {useCallback, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {StoryScreenModel} from '../../../entities';
import {useNotifications} from '../../../shared/lib/notifications/useNotifications';
import {useStoryScreensStore} from '../model/useStoryScreensStore';

interface StoryScreensApiOperations {
  saveScreens: (storyId: string, data: any) => Promise<void>;
  queryKeyPattern: string[];
}

interface UseSaveStoryScreensProps {
  storyId: string;
  screens: StoryScreenModel[];
  apiOperations: StoryScreensApiOperations;
}

// Формат, который ждёт сервер: media вложенным объектом, локальные поля не отправляются
const toRequestScreen = (screen: StoryScreenModel) => {
  let media = null;

  const mediaType = screen.mediaType || screen.media?.type || null;

  if (mediaType === 'image') {
    // Тип — изображение
    if (screen.imageUploadId) {
      // Новое загруженное изображение
      media = {type: 'image' as const, image: {uploadId: screen.imageUploadId, fileName: null}};
    } else if (screen.media?.type === 'image' && screen.media.image.fileName) {
      // Существующее изображение с сервера
      media = {type: 'image' as const, image: {uploadId: null, fileName: screen.media.image.fileName}};
    }
  } else if (mediaType === 'video') {
    // Тип — видео (обложка генерируется на сервере)
    const videoUploadId = screen.videoUploadId || null;
    const videoFileName = !videoUploadId && screen.media?.type === 'video' ? screen.media.video.fileName : null;

    if (videoUploadId || videoFileName) {
      media = {
        type: 'video' as const,
        video: {uploadId: videoUploadId, fileName: videoFileName},
      };
    }
  }

  return {
    media,
    paragraphs: screen.paragraphs,
    button: screen.button,
    theme: screen.theme,
    alignToBottom: screen.alignToBottom,
  };
};

/**
 * Сохранение экранов истории как одна неделимая фаза: отправка на сервер и получение
 * сохранённых данных обратно.
 *
 * Между этими двумя шагами экраны редактировать нельзя: одноразовые токены загрузки уже
 * потрачены, а постоянные имена файлов с сервера ещё не пришли, поэтому правка в этот момент
 * привела бы к отправке экрана без медиа. Если обновление данных не удалось, фаза не считается
 * завершённой — редактор остаётся заблокированным до успешного повтора.
 */
export const useSaveStoryScreens = ({storyId, screens, apiOperations}: UseSaveStoryScreensProps) => {
  const {commitScreens} = useStoryScreensStore();
  const {createNotification} = useNotifications();
  const queryClient = useQueryClient();
  const [isSyncFailed, setIsSyncFailed] = useState(false);
  const [isRetryingSync, setIsRetryingSync] = useState(false);

  const loadSavedScreens = useCallback(
    () => queryClient.invalidateQueries({queryKey: apiOperations.queryKeyPattern}, {throwOnError: true}),
    [queryClient, apiOperations.queryKeyPattern],
  );

  const notifySyncFailed = useCallback(
    (error: unknown) => {
      createNotification({
        title: 'Экраны сохранены, но получить их с сервера не удалось. Повторите обновление.',
        name: 'saveScreensSyncError',
        theme: 'warning',
      });
      console.error('Ошибка при обновлении экранов после сохранения:', error);
    },
    [createNotification],
  );

  const saveScreensMutation = useMutation({
    mutationFn: async ({screens: screensToSave}: {screens: StoryScreenModel[]}) => {
      return apiOperations.saveScreens(storyId, {screens: screensToSave.map(toRequestScreen)});
    },
    onSuccess: async (_result, {screens: savedScreens}) => {
      // Сбрасываем потраченные одноразовые токены загрузки и фиксируем как сохранённое
      // именно отправленный снимок (иначе следующий сейв переотправит мёртвый uploadId → 422)
      commitScreens(storyId, savedScreens);
      createNotification({
        title: 'Экраны успешно сохранены',
        name: 'saveScreens',
        theme: 'success',
      });

      try {
        await loadSavedScreens();
        setIsSyncFailed(false);
      } catch (error) {
        setIsSyncFailed(true);
        notifySyncFailed(error);
      }
    },
    onError: (error: any) => {
      createNotification({
        title: error?.response?.data?.detail || 'Ошибка при сохранении экранов',
        name: 'saveScreensError',
        theme: 'danger',
      });
      console.error('Ошибка при сохранении экранов:', error);
    },
  });

  const saveScreens = useCallback(() => {
    // Снимок отправляемого: стор к моменту ответа сервера может измениться
    saveScreensMutation.mutate({screens});
  }, [saveScreensMutation, screens]);

  const retrySync = useCallback(async () => {
    setIsRetryingSync(true);

    try {
      await loadSavedScreens();
      setIsSyncFailed(false);
    } catch (error) {
      notifySyncFailed(error);
    } finally {
      setIsRetryingSync(false);
    }
  }, [loadSavedScreens, notifySyncFailed]);

  return {
    saveScreens,
    retrySync,
    isSyncFailed,
    // Пока фаза не завершена, любые правки экранов запрещены
    isSaving: saveScreensMutation.isPending || isRetryingSync || isSyncFailed,
  };
};
